// src/pages/AdminWalletRequests.jsx
import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import { useNavigate } from 'react-router-dom';
import { Calendar } from 'react-multi-date-picker';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';
import DateObject from 'react-date-object';
import 'react-multi-date-picker/styles/layouts/mobile.css';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, 
  Filter, 
  Calendar as CalendarIcon, 
  ArrowLeft,
  User,
  Wallet,
  ArrowUpCircle,
  ArrowDownCircle,
  CheckCircle,
  XCircle,
  Copy,
  AlertCircle,
  X,
  Building
} from 'lucide-react';
import Swal from 'sweetalert2';
import { getImageUrl } from '../utils/imageUrl';

// نمایش پیام
const showAlert = (message, type) => {
  Swal.fire({
    text: message,
    icon: type,
    toast: true,
    position: 'top',
    timer: 3000,
    timerProgressBar: true,
    showConfirmButton: false,
    customClass: { popup: 'font-vazir' },
  });
};

// فرمت اعداد
const formatNumber = (value) => {
  if (!value || isNaN(parseFloat(value))) return '0';
  return parseFloat(value).toLocaleString('fa-IR');
};

// تبدیل اعداد فارسی به لاتین
const convertToLatinDigits = (input) => {
  if (!input) return input;
  const persianDigits = [/۰/g, /۱/g, /۲/g, /۳/g, /۴/g, /۵/g, /۶/g, /۷/g, /۸/g, /۹/g];
  const arabicDigits = [/٠/g, /١/g, /٢/g, /٣/g, /٤/g, /٥/g, /٦/g, /٧/g, /٨/g, /٩/g];
  let result = input;
  for (let i = 0; i < 10; i++) {
    result = result.replace(persianDigits[i], i).replace(arabicDigits[i], i);
  }
  return result;
};

// تبدیل تاریخ
const formatDateForBackend = (date, isEndDate = false) => {
  if (!date) return date;
  try {
    const latinDate = convertToLatinDigits(date);
    const dateObject = new DateObject({ date: latinDate, calendar: persian, locale: persian_fa });
    const gregorianDate = dateObject.toDate();
    if (isEndDate) gregorianDate.setHours(23, 59, 59, 999);
    else gregorianDate.setHours(0, 0, 0, 0);
    return gregorianDate.toISOString().replace(/\.\d{3}Z$/, '');
  } catch (err) {
    return date;
  }
};

export default function AdminWalletRequests() {
  const [requests, setRequests] = useState([]);
  const [error, setError] = useState('');
  const [rejectReason, setRejectReason] = useState({});
  const [filters, setFilters] = useState({ userSearch: '', type: '', status: '', startDate: '', endDate: '' });
  const [tempFilters, setTempFilters] = useState({ ...filters });
  const [showStartCalendar, setShowStartCalendar] = useState(false);
  const [showEndCalendar, setShowEndCalendar] = useState(false);
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const startCalendarRef = useRef(null);
  const endCalendarRef = useRef(null);
  const navigate = useNavigate();
  const token = localStorage.getItem('authToken');

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (startCalendarRef.current && !startCalendarRef.current.contains(event.target)) setShowStartCalendar(false);
      if (endCalendarRef.current && !endCalendarRef.current.contains(event.target)) setShowEndCalendar(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const refreshAccessToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) throw new Error('No refresh token');
      const res = await api.post('/api/auth/refresh-token', { refreshToken });
      localStorage.setItem('authToken', res.data.accessToken);
      return res.data.accessToken;
    } catch (err) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      navigate('/login_page');
      return null;
    }
  };

  const fetchRequests = async (appliedFilters) => {
    try {
      const newToken = token || (await refreshAccessToken());
      if (!newToken) throw new Error('No token');
      const res = await api.get('/api/wallet/requests/admin', {
        headers: { Authorization: `Bearer ${newToken}` },
        params: {
          ...appliedFilters,
          startDate: formatDateForBackend(appliedFilters.startDate, false),
          endDate: formatDateForBackend(appliedFilters.endDate, true),
        },
      });
      setRequests(res.data);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'خطا در دریافت درخواست‌ها';
      showAlert(errorMessage, 'error');
    }
  };

  useEffect(() => {
    if (!token) {
      navigate('/login_page');
      return;
    }
    fetchRequests(filters);
  }, [token, navigate]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setTempFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date, field) => {
    if (date) {
      setTempFilters((prev) => ({ ...prev, [field]: date.format('YYYY/MM/DD') }));
    } else {
      setTempFilters((prev) => ({ ...prev, [field]: '' }));
    }
    if (field === 'startDate') setShowStartCalendar(false);
    if (field === 'endDate') setShowEndCalendar(false);
  };

  const getCalendarValue = (date) => {
    try {
      if (date) {
        return new DateObject({
          date: convertToLatinDigits(date),
          calendar: persian,
          locale: persian_fa,
          format: 'YYYY/MM/DD',
        });
      }
      return null;
    } catch {
      return null;
    }
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    if (tempFilters.startDate && tempFilters.endDate) {
      const start = new DateObject({ date: convertToLatinDigits(tempFilters.startDate), calendar: persian, locale: persian_fa }).toDate();
      const end = new DateObject({ date: convertToLatinDigits(tempFilters.endDate), calendar: persian, locale: persian_fa }).toDate();
      if (start > end) {
        showAlert('تاریخ شروع نمی‌تواند بعد از تاریخ پایان باشد', 'error');
        return;
      }
    }
    setFilters(tempFilters);
    fetchRequests(tempFilters);
    setShowFiltersMobile(false);
  };

  const handleResetFilters = () => {
    const reset = { userSearch: '', type: '', status: '', startDate: '', endDate: '' };
    setTempFilters(reset);
    setFilters(reset);
    fetchRequests(reset);
    setShowFiltersMobile(false);
  };

  const handleUpdate = async (id, status, type) => {
    try {
      const newToken = token || (await refreshAccessToken());
      if (!newToken) throw new Error('No token');
      const body = { status };
      if (status === 'rejected') {
        body.reject_reason = rejectReason[id] || 'دلیل رد مشخص نشده';
      }
      const res = await api.put(`/api/wallet/requests/${id}`, body, {
        headers: { Authorization: `Bearer ${newToken}` },
      });
      showAlert(res.data.message, 'success');
      fetchRequests(filters);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'خطا در به‌روزرسانی درخواست';
      showAlert(errorMessage, 'error');
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'در انتظار';
      case 'approved': return 'تأیید شده';
      case 'rejected': return 'رد شده';
      case 'canceled': return 'لغو شده';
      default: return status;
    }
  };

  const getTypeText = (type) => (type === 'deposit' ? 'شارژ' : 'برداشت');

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 border-yellow-300 text-yellow-800';
      case 'approved': return 'bg-green-100 border-green-300 text-green-800';
      case 'rejected': return 'bg-red-100 border-red-300 text-red-800';
      case 'canceled': return 'bg-gray-100 border-gray-300 text-gray-800';
      default: return 'bg-gray-100 border-gray-300 text-gray-800';
    }
  };

  // کپی بدون IR
  const copyToClipboard = (text) => {
    const cleanText = text.replace(/^IR/i, '').trim();
    navigator.clipboard.writeText(cleanText).then(() => {
      showAlert('کپی شد!', 'success');
    }).catch(() => {
      showAlert('خطا در کپی', 'error');
    });
  };

  // کامپوننت جمع و جور برای بانک
  const BankName = ({ value }) => {
    if (!value) return null;
    return (
      <div className="flex items-center gap-2 text-sm">
        <Building className="w-4 h-4 text-gray-500" />
        <span className="font-medium text-gray-700">{value}</span>
      </div>
    );
  };

  // کامپوننت جمع و جور برای کارت/شبا
  const BankDetail = ({ label, value, copyText }) => {
    if (!value) return null;
    return (
      <div className="flex items-center justify-between bg-gray-50 p-2 rounded-lg text-xs">
        <span className="text-gray-600">{label}:</span>
        <div className="flex items-center gap-1">
          <span className="font-mono text-gray-800">{value}</span>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => copyToClipboard(copyText || value)}
            className="text-indigo-600"
          >
            <Copy className="w-3.5 h-3.5" />
          </motion.button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 px-4 py-8 font-vazir">
      <div className="max-w-7xl mx-auto">

        {/* برگشت */}
        <motion.button
          whileHover={{ x: -5 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-indigo-600 mb-6 text-sm font-medium"
        >
          <ArrowLeft className="w-5 h-5" />
          برگشت
        </motion.button>

        {/* هدر */}
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl flex items-center justify-center text-white shadow-xl">
              <Wallet className="w-9 h-9" />
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-800">مدیریت درخواست‌های کیف پول</h2>
          <p className="text-gray-600 mt-2">بررسی و تأیید/رد درخواست‌های شارژ و برداشت</p>
        </motion.div>

        {/* پیام‌ها */}
        <AnimatePresence>
          {error && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-2xl mb-6 flex items-center gap-3">
              <AlertCircle className="w-6 h-6" />
              {error}
            </motion.div>
          )}
        </AnimatePresence>

        {/* دکمه فیلتر موبایل */}
        <div className="sm:hidden mb-6">
          <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
            onClick={() => setShowFiltersMobile(!showFiltersMobile)}
            className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white py-3 px-4 rounded-2xl flex items-center justify-center gap-2 shadow-lg">
            <Filter className="w-5 h-5" />
            فیلترها
          </motion.button>
        </div>

        {/* فیلترها */}
        <AnimatePresence>
          {(showFiltersMobile || !isMobile) && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
              className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl p-6 mb-8 border border-white/30">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-800 flex items-center gap-3">
                  <Filter className="w-6 h-6 text-indigo-600" />
                  فیلترهای پیشرفته
                </h3>
                {isMobile && (
                  <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                    onClick={() => setShowFiltersMobile(false)} className="text-gray-500">
                    <X className="w-6 h-6" />
                  </motion.button>
                )}
              </div>
              <form onSubmit={handleFilterSubmit} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">جستجوی کاربر</label>
                  <div className="relative">
                    <input type="text" name="userSearch" value={tempFilters.userSearch} onChange={handleFilterChange}
                      placeholder="نام یا شناسه"
                      className="w-full pl-10 pr-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">نوع درخواست</label>
                  <select name="type" value={tempFilters.type} onChange={handleFilterChange}
                    className="w-full px-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500">
                    <option value="">همه</option>
                    <option value="deposit">شارژ</option>
                    <option value="withdraw">برداشت</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">وضعیت</label>
                  <select name="status" value={tempFilters.status} onChange={handleFilterChange}
                    className="w-full px-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500">
                    <option value="">همه</option>
                    <option value="pending">در انتظار</option>
                    <option value="approved">تأیید شده</option>
                    <option value="rejected">رد شده</option>
                    <option value="canceled">لغو شده</option>
                  </select>
                </div>
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">از تاریخ</label>
                  <div className="flex items-center gap-2">
                    <input type="text" name="startDate" value={tempFilters.startDate} onChange={handleFilterChange}
                      placeholder="1404/01/01"
                      className="flex-1 px-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                    <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} type="button"
                      onClick={() => setShowStartCalendar(!showStartCalendar)}
                      className="p-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl shadow-lg">
                      <CalendarIcon className="w-5 h-5" />
                    </motion.button>
                  </div>
                  {showStartCalendar && (
                    <motion.div ref={startCalendarRef} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      className="absolute z-50 mt-2 right-0 bg-white rounded-2xl shadow-2xl p-4 border">
                      <Calendar calendar={persian} locale={persian_fa} value={getCalendarValue(tempFilters.startDate)}
                        onChange={(date) => handleDateChange(date, 'startDate')} className="rmdp-mobile" />
                    </motion.div>
                  )}
                </div>
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">تا تاریخ</label>
                  <div className="flex items-center gap-2">
                    <input type="text" name="endDate" value={tempFilters.endDate} onChange={handleFilterChange}
                      placeholder="1404/01/01"
                      className="flex-1 px-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                    <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} type="button"
                      onClick={() => setShowEndCalendar(!showEndCalendar)}
                      className="p-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl shadow-lg">
                      <CalendarIcon className="w-5 h-5" />
                    </motion.button>
                  </div>
                  {showEndCalendar && (
                    <motion.div ref={endCalendarRef} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      className="absolute z-50 mt-2 right-0 bg-white rounded-2xl shadow-2xl p-4 border">
                      <Calendar calendar={persian} locale={persian_fa} value={getCalendarValue(tempFilters.endDate)}
                        onChange={(date) => handleDateChange(date, 'endDate')} className="rmdp-mobile" />
                    </motion.div>
                  )}
                </div>
                <div className="flex gap-3 items-end">
                  <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} type="submit"
                    className="flex-1 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-bold rounded-xl shadow-lg flex items-center justify-center gap-2">
                    <Filter className="w-5 h-5" /> اعمال
                  </motion.button>
                  <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} type="button" onClick={handleResetFilters}
                    className="flex-1 py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white font-bold rounded-xl shadow-lg flex items-center justify-center gap-2">
                    <XCircle className="w-5 h-5" /> پاک کردن
                  </motion.button>
                </div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>

        {/* جدول */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl p-6 border border-white/30 overflow-hidden">
          <div className="flex items-center justify-between mb-6">
            <p className="text-lg font-semibold text-gray-700">
              تعداد درخواست‌ها: <span className="text-indigo-600">{requests.length}</span>
            </p>
          </div>

          {/* دسکتاپ */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gradient-to-r from-indigo-50 to-purple-50 text-gray-700">
                  <th className="p-3 text-right font-bold">شناسه</th>
                  <th className="p-3 text-right font-bold">کاربر</th>
                  <th className="p-3 text-right font-bold">نوع</th>
                  <th className="p-3 text-right font-bold">مبلغ</th>
                  <th className="p-3 text-right font-bold">بانک</th>
                  <th className="p-3 text-right font-bold">کارت/شبا</th>
                  <th className="p-3 text-right font-bold">فیش</th>
                  <th className="p-3 text-right font-bold">وضعیت</th>
                  <th className="p-3 text-right font-bold">عملیات</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((req) => (
                  <motion.tr key={req.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} whileHover={{ backgroundColor: '#f8fafc' }} className="border-b">
                    <td className="p-3 font-mono text-indigo-600">#{req.id}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-500" />
                        <div>
                          <p className="font-medium text-sm">{req.first_name} {req.last_name}</p>
                          <p className="text-xs text-gray-500">ID: {req.user_id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${req.type === 'deposit' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                        {req.type === 'deposit' ? <ArrowUpCircle className="w-3.5 h-3.5" /> : <ArrowDownCircle className="w-3.5 h-3.5" />}
                        {getTypeText(req.type)}
                      </span>
                    </td>
                    <td className="p-3 font-mono text-gray-900">
                      {formatNumber(req.amount)} <span className="text-xs text-gray-500">ریال</span>
                    </td>
                    <td className="p-3">
                      <BankName value={req.bank_name} />
                      {req.account_holder_name}
                    </td>
                    <td className="p-3 space-y-1">
                      {req.card_number && <BankDetail label="کارت" value={req.card_number} copyText={req.card_number} />}
                      {req.iban && <BankDetail label="شبا" value={req.iban} copyText={req.iban} />}
                      {!req.card_number && !req.iban && <span className="text-gray-400 text-xs">—</span>}
                    </td>
                    <td className="p-3">
                      {req.receipt_image ? (
                        <img src={getImageUrl(req.receipt_image)} alt="فیش"
                          className="w-12 h-12 object-cover rounded-lg cursor-pointer hover:scale-105 transition-transform shadow-md"
                          onClick={() => window.open(getImageUrl(req.receipt_image), '_blank')} />
                      ) : (
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                          <span className="text-xs text-gray-500">—</span>
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(req.status)}`}>
                        {getStatusText(req.status)}
                      </span>
                    </td>
                    <td className="p-3">
                      {req.status === 'pending' && (
                        <div className="flex flex-col gap-1">
                          <button onClick={() => handleUpdate(req.id, 'approved', req.type)}
                            className={`${req.type === 'deposit' ? 'bg-green-500 hover:bg-green-600' : 'bg-blue-500 hover:bg-blue-600'} text-white py-1.5 px-3 rounded-lg font-medium text-xs transition-all shadow-sm hover:shadow-md`}>
                            {req.type === 'deposit' ? 'تأیید شارژ' : 'تأیید برداشت'}
                          </button>
                          <div className="flex gap-1">
                            <input type="text" placeholder="دلیل رد" value={rejectReason[req.id] || ''}
                              onChange={(e) => setRejectReason({ ...rejectReason, [req.id]: e.target.value })}
                              className="flex-1 p-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-red-500" />
                            <button onClick={() => handleUpdate(req.id, 'rejected', req.type)}
                              className="bg-red-500 hover:bg-red-600 text-white py-1.5 px-2 rounded-lg text-xs font-medium transition-all shadow-sm hover:shadow-md">
                              رد
                            </button>
                          </div>
                        </div>
                      )}
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* موبایل */}
          <div className="lg:hidden divide-y divide-gray-200">
            {requests.map((req) => (
              <motion.div key={req.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} whileHover={{ backgroundColor: '#f8fafc' }}
                className={`p-3 ${getStatusColor(req.status)}`}>
                <div className="flex justify-between items-start mb-2">
                  <span className="bg-white px-2 py-0.5 rounded-full text-xs font-bold text-gray-800">#{req.id}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${req.type === 'deposit' ? 'bg-green-200 text-green-800' : 'bg-blue-200 text-blue-800'}`}>
                    {getTypeText(req.type)}
                  </span>
                </div>

                <div className="mb-2">
                  <p className="font-bold text-base text-gray-900 mb-1">
                    {formatNumber(req.amount)} <span className="text-xs text-gray-500">ریال</span>
                  </p>
                  <p className="text-sm text-gray-600">{req.first_name} {req.last_name}</p>
                  <p className="text-xs text-gray-500">ID: {req.user_id}</p>
                </div>

                {/* بانک + جزئیات */}
                <div className="space-y-1 mb-2">
                  {req.bank_name && <BankName value={req.bank_name} />}
                  {req.card_number && <BankDetail label="کارت" value={req.card_number} copyText={req.card_number} />}
                  {req.iban && <BankDetail label="شبا" value={req.iban} copyText={req.iban} />}
                </div>

                <div className="flex items-center justify-between mb-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${getStatusColor(req.status)}`}>
                    {getStatusText(req.status)}
                  </span>
                  {req.receipt_image ? (
                    <img src={getImageUrl(req.receipt_image)} alt="فیش"
                      className="w-14 h-14 object-cover rounded-xl cursor-pointer hover:scale-105 transition-transform shadow-md"
                      onClick={() => window.open(getImageUrl(req.receipt_image), '_blank')} />
                  ) : (
                    <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center">
                      <span className="text-xs text-gray-500">—</span>
                    </div>
                  )}
                </div>

                {req.status === 'pending' && (
                  <div className="space-y-1 pt-2 border-t border-gray-200">
                    <button onClick={() => handleUpdate(req.id, 'approved', req.type)}
                      className={`${req.type === 'deposit' ? 'bg-green-500 hover:bg-green-600' : 'bg-blue-500 hover:bg-blue-600'} w-full text-white py-2.5 px-3 rounded-xl font-medium text-sm shadow-lg hover:shadow-xl transition-all`}>
                      {req.type === 'deposit' ? 'تأیید شارژ' : 'تأیید برداشت'}
                    </button>
                    <div className="flex gap-1">
                      <input type="text" placeholder="دلیل رد" value={rejectReason[req.id] || ''}
                        onChange={(e) => setRejectReason({ ...rejectReason, [req.id]: e.target.value })}
                        className="flex-1 p-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-red-500" />
                      <button onClick={() => handleUpdate(req.id, 'rejected', req.type)}
                        className="bg-red-500 hover:bg-red-600 text-white py-2.5 px-3 rounded-xl text-sm font-medium shadow-lg hover:shadow-xl transition-all">
                        رد
                      </button>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>

        {requests.length === 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-12">
            <div className="w-24 h-24 bg-gray-100 rounded-3xl mx-auto mb-6 flex items-center justify-center">
              <span className="text-3xl text-gray-400">No requests</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">هیچ درخواستی یافت نشد</h3>
            <p className="text-gray-500">فیلترها را تغییر دهید یا صبر کنید تا درخواست جدیدی ثبت شود</p>
          </motion.div>
        )}
      </div>

      <style jsx>{`.font-vazir { font-family: 'Vazir', sans-serif !important; }`}</style>
    </div>
  );
}